import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { SectionHeader } from "../shared/SectionHeader";
import { SettingsInput, labelClass } from "../shared/SettingsInput";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

const schema = z.object({
  name: z.string().min(1, "Business name is required"),
  trading_name: z.string().optional(),
  business_type: z.string().optional(),
  industry: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
});

type FormValues = z.infer<typeof schema>;

const BUSINESS_TYPES = ["Sole Trader", "Partnership", "Private Limited", "NGO"];
const INDUSTRIES = [
  "Retail General",
  "Retail Hardware & Construction",
  "Retail Clothing & Apparel",
  "Retail Electronics",
  "Retail Food & Grocery",
  "Retail Pharmacy & Health",
  "Wholesale & Distribution",
  "Food & Beverage",
  "Hospitality & Accommodation",
  "Professional Services",
  "Agriculture & Farming",
  "Other",
];

export function BusinessProfile() {
  const { businessId } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isDirty },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      trading_name: "",
      business_type: "",
      industry: "",
      phone: "",
      email: "",
    },
  });

  useEffect(() => {
    if (!businessId) return;
    Promise.all([
      supabase.from("businesses").select("name").eq("id", businessId).maybeSingle(),
      supabase.from("business_profiles").select("trading_name, business_type, industry, phone_number, business_email, updated_at").eq("business_id", businessId).maybeSingle(),
    ]).then(([businessRes, profileRes]) => {
      const name = (businessRes.data as { name: string | null } | null)?.name ?? "";
      const profile = profileRes.data as Record<string, unknown> | null;
      reset({
        name,
        trading_name: (profile?.trading_name as string) ?? "",
        business_type: (profile?.business_type as string) ?? "",
        industry: (profile?.industry as string) ?? "",
        phone: (profile?.phone_number as string) ?? "",
        email: (profile?.business_email as string) ?? "",
      });
      setLastUpdated(profile?.updated_at ? new Date(profile.updated_at as string).toISOString() : null);
    });
  }, [businessId, reset]);

  const onSubmit = async (values: FormValues) => {
    if (!businessId) return;

    const { error: err1 } = await supabase
      .from("businesses")
      .update({ name: values.name })
      .eq("id", businessId);
    if (err1) {
      toast({ title: "Error", description: err1.message, variant: "destructive" });
      return;
    }

    const { error: err2 } = await supabase
      .from("business_profiles")
      .upsert(
        {
          business_id: businessId,
          trading_name: values.trading_name || null,
          business_type: values.business_type || null,
          industry: values.industry || null,
          phone_number: values.phone || null,
          business_email: values.email || null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "business_id" }
      );
    if (err2) {
      toast({ title: "Error", description: err2.message, variant: "destructive" });
      return;
    }

    const { data: primaryBranch } = await supabase
      .from("branches")
      .select("id")
      .eq("business_id", businessId)
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingle();
    if (primaryBranch?.id) {
      const { error: err3 } = await supabase
        .from("branches")
        .update({ name: values.name })
        .eq("id", primaryBranch.id);
      if (err3) {
        toast({ title: "Error", description: err3.message, variant: "destructive" });
        return;
      }
    }

    queryClient.invalidateQueries({ queryKey: ["business-name"] });
    toast({ title: "Business profile updated" });
    setLastUpdated(new Date().toISOString());
    reset(values);
  };

  return (
    <>
      <SectionHeader
        title="Business Profile"
        description="Your business details shown to your team and in reports."
      />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label className={labelClass}>Business Name</Label>
          <SettingsInput {...register("name")} placeholder="e.g. Angelic Store" />
          {errors.name && <p className="text-[11px] text-red-400 mt-1">{errors.name.message}</p>}
        </div>
        <div>
          <Label className={labelClass}>Trading Name</Label>
          <SettingsInput {...register("trading_name")} placeholder="Optional" />
        </div>
        <div>
          <Label className={labelClass}>Business Type</Label>
          <Select onValueChange={(v) => setValue("business_type", v, { shouldDirty: true })} value={watch("business_type")}>
            <SelectTrigger className="bg-background border-border">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {BUSINESS_TYPES.map((t) => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={labelClass}>Industry</Label>
          <Select onValueChange={(v) => setValue("industry", v, { shouldDirty: true })} value={watch("industry")}>
            <SelectTrigger className="bg-background border-border">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {INDUSTRIES.map((i) => (
                <SelectItem key={i} value={i}>{i}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={labelClass}>Phone Number</Label>
          <SettingsInput {...register("phone")} placeholder="+260 97 000 0000" />
        </div>
        <div>
          <Label className={labelClass}>Business Email</Label>
          <SettingsInput {...register("email")} type="email" placeholder="Optional" />
        </div>
        {isDirty && (
          <Button type="submit">
            Save Changes
          </Button>
        )}
        {lastUpdated && (
          <p className="font-mono text-[11px] text-muted-foreground mt-2">
            Last updated {new Date(lastUpdated).toLocaleString()}
          </p>
        )}
      </form>
    </>
  );
}
